import Vue from 'vue';
import { httpRequest } from '../services/httpRequest';

/**
 * Activity Tracker Mixin
 * Tracks user activity (mouse, keyboard, clicks) and sends keep-alive signals
 * to the server every 1 minute if user has been active.
 * Supports multi-tab scenarios - activity in any tab extends session for all tabs.
 */

// BroadcastChannel for cross-tab activity synchronization
const activityChannel = new BroadcastChannel('userActivity');

export const ActivityTrackerMixin = Vue.extend({
    data() {
        return {
            // Flag to track if user has been active since last keep-alive
            hasActivity: false,
            
            // Interval timer reference for keep-alive checks
            keepAliveInterval: null,

            // Prevent slow keep-alive requests from overlapping each other
            keepAliveInProgress: false,
            
            // Keep-alive interval duration (1 minute for good sync with manageable server load)
            keepAliveIntervalDuration: 1 * 60 * 1000, // 1 minute in milliseconds
            
            // Debounce timeout for activity detection
            activityDebounceTimeout: null,
            
            // Debounce duration (1 second - avoid excessive flag updates)
            activityDebounceDuration: 1000,
        };
    },
    
    mounted() {
        this.initActivityTracking();
        this.initCrossTabActivitySync();
    },
    
    beforeDestroy() {
        this.cleanupActivityTracking();
        this.cleanupCrossTabActivitySync();
    },
    
    methods: {
        /**
         * Initialize activity tracking and keep-alive timer
         */
        initActivityTracking() {
            // Add event listeners for user activity
            window.addEventListener('mousemove', this.onUserActivity);
            window.addEventListener('keydown', this.onUserActivity);
            window.addEventListener('click', this.onUserActivity);
            window.addEventListener('scroll', this.onUserActivity);
            window.addEventListener('touchstart', this.onUserActivity);
            
            // Start keep-alive interval check (every 1 minute)
            this.keepAliveInterval = window.setInterval(() => {
                this.checkAndSendKeepAlive();
            }, this.keepAliveIntervalDuration);
            
        },
        
        /**
         * Clean up event listeners and timers
         */
        cleanupActivityTracking() {
            // Remove event listeners
            window.removeEventListener('mousemove', this.onUserActivity);
            window.removeEventListener('keydown', this.onUserActivity);
            window.removeEventListener('click', this.onUserActivity);
            window.removeEventListener('scroll', this.onUserActivity);
            window.removeEventListener('touchstart', this.onUserActivity);
            
            // Clear intervals and timeouts
            if (this.keepAliveInterval) {
                clearInterval(this.keepAliveInterval);
                this.keepAliveInterval = null;
            }
            
            if (this.activityDebounceTimeout) {
                clearTimeout(this.activityDebounceTimeout);
                this.activityDebounceTimeout = null;
            }
            
        },
        
        /**
         * Initialize cross-tab activity synchronization
         */
        initCrossTabActivitySync() {
            // Listen for activity from other tabs
            activityChannel.onmessage = (event) => {
                if (event.data.type === 'activity') {
                    if (!this.hasActivity) {
                        this.hasActivity = true;
                    }
                    // Update localStorage to share activity timestamp across tabs
                    localStorage.setItem('lastUserActivity', event.data.timestamp);
                }
            };
            
            // Check localStorage for recent activity from other tabs on mount
            const lastActivity = localStorage.getItem('lastUserActivity');
            if (lastActivity) {
                const lastActivityTime = parseInt(lastActivity, 10);
                const timeSinceActivity = Date.now() - lastActivityTime;
                // If activity within last 30 seconds, consider user active
                if (timeSinceActivity < 30000) {
                    this.hasActivity = true;
                }
            }
        },
        
        /**
         * Cleanup cross-tab activity synchronization
         */
        cleanupCrossTabActivitySync() {
            activityChannel.onmessage = null;
        },
        
        /**
         * Handle user activity events (debounced)
         */
        onUserActivity() {
            // Clear existing debounce timeout
            if (this.activityDebounceTimeout) {
                clearTimeout(this.activityDebounceTimeout);
            }
            
            // Set debounced flag update
            this.activityDebounceTimeout = window.setTimeout(() => {
                if (!this.hasActivity) {
                    this.hasActivity = true;
                }
                
                // // Broadcast activity to other tabs
                // const timestamp = Date.now().toString();
                // activityChannel.postMessage({ type: 'activity', timestamp });
                // localStorage.setItem('lastUserActivity', timestamp);
                
                // Also update sessionStorage for compatibility with existing code
                sessionStorage.setItem('recentActivityDone', 'true');
            }, this.activityDebounceDuration);
        },
        
        /**
         * Check if user has been active and send keep-alive if needed
         */
        async checkAndSendKeepAlive() {
            if (this.hasActivity && !this.keepAliveInProgress && sessionStorage.getItem('loggedIn') === 'true') {
                this.keepAliveInProgress = true;
                
                try {
                    await this.sendKeepAlive();
                    
                    // Reset activity only after successful keep-alive
                    const timestamp = Date.now().toString();
                    try { activityChannel.postMessage({ type: 'resetactivity', timestamp }); } catch (e) { /* channel closed */ }
                    sessionStorage.setItem('recentActivityDone', 'true');
                    localStorage.setItem('lastUserActivity', timestamp);
                    this.hasActivity = false; 
                } catch (error) {
                    this.hasActivity = false; 
                } finally {
                    this.keepAliveInProgress = false;
                }
            }
        },
        
        /**
         * Send keep-alive request to server
         */
        async sendKeepAlive() {
            return httpRequest.post('Session/KeepAlive', {}, { showError: false });
        },
        
        /**
         * Manually trigger keep-alive (can be called from components if needed)
         */
        async triggerKeepAlive() {
            this.hasActivity = true;
            await this.checkAndSendKeepAlive();
        },
        
        /**
         * Get current activity status (for debugging)
         */
        getActivityStatus() {
            return {
                hasActivity: this.hasActivity,
                keepAliveInterval: this.keepAliveInterval !== null,
                keepAliveIntervalMinutes: this.keepAliveIntervalDuration / 60000,
            };
        },
    },
});
